import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2, Copy, Mail, Globe, MapPin, Building2, Send, CheckCircle2, Eye, MousePointerClick, AlertTriangle, MessageCircle, Users, Pin, CheckSquare, Square, Clock, Plus, Trash2, Calendar, FileText, ShieldCheck, XCircle, RefreshCw } from "lucide-react";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const seconds = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();

  const [lead, setLead] = useState(null);
  const [emails, setEmails] = useState([]);
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [pipeline, setPipeline] = useState(null);

  const [tone, setTone] = useState("professional");
  const [offer, setOffer] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  
  // New note/task state
  const [newNote, setNewNote] = useState("");
  const [newTask, setNewTask] = useState("");
  
  // Edit CRM fields
  const [editingDeal, setEditingDeal] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dealValueInput, setDealValueInput] = useState("");

  const toggleDnd = async (currentVal) => {
    try {
      await api.patch(`/leads/${id}/dnd`, { dnd: !currentVal });
      toast.success(!currentVal ? "Lead marked Do Not Disturb" : "DND removed");
      load();
    } catch (e) { toast.error("Failed to update DND status"); }
  };

  const verifyEmail = async () => {
    if (!lead?.email) { toast.error("No email address to verify"); return; }
    setVerifying(true);
    try {
      const r = await api.post(`/leads/${id}/verify`);
      const st = r.data.verification_status;
      if (st === "valid") toast.success("Email is deliverable!");
      else if (st === "catch_all") toast("Catch-all domain — delivery uncertain");
      else if (st === "invalid") toast.error("Email is undeliverable");
      else toast("Could not determine — SMTP check inconclusive");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setVerifying(false); }
  };

  const load = async () => {
    try {
      const [lRes, eRes, pRes, aRes, nRes, tRes] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get(`/outreach/lead/${id}`),
        api.get('/crm/pipeline'),
        api.get(`/crm/leads/${id}/activities`),
        api.get(`/crm/leads/${id}/notes`),
        api.get(`/crm/leads/${id}/tasks`)
      ]);
      setLead(lRes.data);
      setDealValueInput(lRes.data.deal_value || "");
      setEmails(eRes.data);
      setPipeline(pRes.data);
      setActivities(aRes.data);
      setNotes(nRes.data);
      setTasks(tRes.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load profile data");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // CRM Actions
  const updateStage = async (stageId) => {
    try {
      await api.patch(`/crm/leads/${id}/stage`, { pipeline_stage: stageId });
      toast.success("Stage updated");
      load();
    } catch (e) { toast.error("Failed to update stage"); }
  };

  const updateDealValue = async () => {
    try {
      const val = parseFloat(dealValueInput);
      await api.patch(`/crm/leads/${id}/deal`, { deal_value: isNaN(val) ? null : val });
      setEditingDeal(false);
      toast.success("Deal value updated");
      load();
    } catch (e) { toast.error("Failed to update deal value"); }
  };

  // Notes
  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/crm/leads/${id}/notes`, { body: newNote });
      setNewNote("");
      load();
    } catch (e) { toast.error("Failed to add note"); }
  };
  
  const togglePinNote = async (noteId, currentPin) => {
    await api.patch(`/crm/notes/${noteId}`, { pinned: !currentPin });
    load();
  };

  const deleteNote = async (noteId) => {
    await api.delete(`/crm/notes/${noteId}`);
    load();
  };

  // Tasks
  const addTask = async () => {
    if (!newTask.trim()) return;
    try {
      await api.post(`/crm/leads/${id}/tasks`, { title: newTask });
      setNewTask("");
      load();
    } catch (e) { toast.error("Failed to add task"); }
  };

  const toggleTask = async (taskId, currentStatus) => {
    await api.patch(`/crm/tasks/${taskId}`, { completed: !currentStatus });
    load();
  };

  // Email outreach actions
  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/outreach/generate", { lead_id: id, tone, offer });
      toast.success("Email drafted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Drafting failed");
    } finally { setGenerating(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const sendEmail = async (eid) => {
    if (!user?.has_smtp) { toast.error("Configure your email provider in Settings first"); return; }
    if (!user?.from_email) { toast.error("Configure your From email in Settings first"); return; }
    if (!lead?.email) { toast.error("This prospect has no email address"); return; }
    setSendingId(eid);
    try {
      await api.post(`/outreach/${eid}/send`);
      toast.success("Email sent via Resend");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSendingId(null); }
  };

  const toggleReplied = async (eid, currentlyReplied) => {
    try {
      if (currentlyReplied) {
        await api.post(`/outreach/${eid}/unmark-replied`);
        toast.success("Reply unmarked");
      } else {
        await api.post(`/outreach/${eid}/mark-replied`);
        toast.success("Marked as replied");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!lead) return <div className="p-10 text-[#5C5C66]">Loading...</div>;

  const formatCurrency = (val) => {
    if (!val) return "$0";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  };

  // Merge emails and activities into a single chronological timeline
  const timelineEvents = [
    ...activities.map(a => ({ ...a, event_type: 'activity', date: a.created_at })),
    ...emails.map(e => ({ ...e, event_type: 'email', date: e.created_at }))
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1600px] mx-auto h-[calc(100vh-1rem)] overflow-y-auto">
      <Link to="/app/leads" className="text-sm text-[#5C5C66] hover:text-[#0A0A0F] inline-flex items-center gap-2 mb-6 transition-colors" data-testid="back-to-leads">
        <ArrowLeft className="h-4 w-4" /> Back to pipeline
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* LEFT PANEL: CRM Profile */}
        <div className="lg:col-span-3 space-y-5 sticky top-0">
          <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft">
            <div className="flex items-center justify-between mb-4">
              <div className="label-mono">Score</div>
              <div className="font-mono font-black text-3xl text-[#002FA7]">{lead.score}</div>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#002FA7] to-[#1E4FD6] text-white flex items-center justify-center text-sm font-bold shrink-0 shadow-inner">
                {lead.name.split(" ").map(w => w[0]).join("").slice(0, 2)}
              </div>
              <div className="min-w-0">
                <div className="font-display text-xl font-bold tracking-tighter truncate flex items-center gap-2">
                  {lead.name}
                  {lead.dnd && <Badge className="bg-red-50 text-red-600 border-red-200 uppercase text-[10px] tracking-wider">Opted-Out</Badge>}
                </div>
                <div className="text-sm text-[#5C5C66] truncate">{lead.title}</div>
              </div>
            </div>

            {/* Editable CRM Fields */}
            <div className="space-y-4 mt-6">
              <div>
                <Label className="label-mono mb-1.5 block">Pipeline Stage</Label>
                <Select value={lead.pipeline_stage || "s_new"} onValueChange={updateStage}>
                  <SelectTrigger className="w-full bg-[#FAFAF7]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pipeline?.stages?.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                          {s.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="label-mono mb-1.5 block">Deal Value</Label>
                {editingDeal ? (
                  <div className="flex gap-2">
                    <Input type="number" value={dealValueInput} onChange={e => setDealValueInput(e.target.value)} placeholder="0" className="bg-[#FAFAF7]" />
                    <Button onClick={updateDealValue} size="sm" className="bg-[#0A0A0F] text-white">Save</Button>
                  </div>
                ) : (
                  <div 
                    className="p-2.5 bg-[#FAFAF7] border border-[#EAEAE3] rounded-lg font-mono text-sm cursor-pointer hover:border-[#002FA7]/30 transition-colors"
                    onClick={() => setEditingDeal(true)}
                  >
                    {formatCurrency(lead.deal_value)}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-3 bg-[#FAFAF7] border border-[#EAEAE3] rounded-lg">
                <div>
                  <Label className="font-bold text-[#0A0A0F] block">Do Not Disturb</Label>
                  <div className="text-xs text-[#5C5C66]">Opt out of sequences</div>
                </div>
                <div 
                  className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${lead.dnd ? 'bg-red-500' : 'bg-gray-300'}`}
                  onClick={() => toggleDnd(lead.dnd)}
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm absolute top-0.5 transition-all ${lead.dnd ? 'left-5' : 'left-0.5'}`} />
                </div>
              </div>
            </div>

            <div className="space-y-2.5 text-sm border-t border-[#EAEAE3] mt-6 pt-5">
              {lead.company && <Row icon={Building2} label="Company" value={lead.company} />}
              {lead.email && (
                <div className="flex items-start gap-2">
                  <Mail className="h-3.5 w-3.5 text-[#9A9AA3] mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="label-mono text-[10px]">Email</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="break-all">{lead.email}</span>
                      {lead.verification_status === "valid" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-md bg-green-50 text-green-700 border border-green-200 animate-in fade-in-0 duration-300">
                          <CheckCircle2 className="h-2.5 w-2.5" /> Deliverable
                        </span>
                      )}
                      {lead.verification_status === "invalid" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-md bg-red-50 text-red-600 border border-red-200 animate-in fade-in-0 duration-300">
                          <XCircle className="h-2.5 w-2.5" /> Undeliverable
                        </span>
                      )}
                      {lead.verification_status === "catch_all" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 animate-in fade-in-0 duration-300">
                          <AlertTriangle className="h-2.5 w-2.5" /> Catch-All
                        </span>
                      )}
                      {lead.verification_status === "unknown" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-500 border border-slate-200">
                          <ShieldCheck className="h-2.5 w-2.5" /> Unknown
                        </span>
                      )}
                    </div>
                    {(!lead.verification_status || lead.verification_status === "unverified" || lead.verification_status === "unknown") && (
                      <button
                        onClick={verifyEmail}
                        disabled={verifying}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-[#002FA7] hover:text-[#0040D6] transition-colors disabled:opacity-50"
                      >
                        {verifying ? (
                          <><Loader2 className="h-2.5 w-2.5 animate-spin" /> Verifying...</>
                        ) : (
                          <><RefreshCw className="h-2.5 w-2.5" /> Verify address</>
                        )}
                      </button>
                    )}
                    {lead.verification_error && lead.verification_status !== "valid" && (
                      <div className="text-[10px] text-[#9A9AA3] mt-0.5">{lead.verification_error}</div>
                    )}
                  </div>
                </div>
              )}
              {lead.website && <Row icon={Globe} label="Website" value={lead.website} link />}
              {lead.location && <Row icon={MapPin} label="Location" value={lead.location} />}
              {lead.industry && <Row icon={Building2} label="Industry" value={lead.industry} />}
            </div>
          </div>

          {(lead.summary || lead.pain_points) && (
            <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft">
              {lead.summary && (<><div className="label-mono mb-2">— Summary</div><p className="text-sm leading-relaxed mb-4">{lead.summary}</p></>)}
              {lead.pain_points && (<><div className="label-mono mb-2">— Likely pain points</div><p className="text-sm leading-relaxed">{lead.pain_points}</p></>)}
            </div>
          )}
        </div>

        {/* CENTER PANEL: Activity Timeline */}
        <div className="lg:col-span-5 space-y-5">
          <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft h-full">
            <div className="flex items-center justify-between mb-6">
              <div className="font-display text-xl font-bold tracking-tight">Activity Timeline</div>
              <Badge variant="secondary" className="font-mono text-xs">{timelineEvents.length} events</Badge>
            </div>
            
            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-4 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-[#EAEAE3] before:to-transparent">
              {timelineEvents.length === 0 ? (
                <div className="text-center text-sm text-[#9A9AA3] py-10">No activity yet.</div>
              ) : timelineEvents.map((evt) => (
                <div key={evt.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full border-4 border-white bg-[#F4F4EE] text-[#5C5C66] shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10">
                    {evt.event_type === 'email' ? <Mail className="w-3.5 h-3.5" /> : 
                     evt.type === 'stage_changed' ? <Sparkles className="w-3.5 h-3.5 text-amber-500" /> :
                     <Clock className="w-3.5 h-3.5" />}
                  </div>
                  
                  <div className="w-[calc(100%-2.5rem)] md:w-[calc(50%-2rem)] p-4 rounded-xl border border-[#EAEAE3] bg-[#FAFAF7] shadow-sm">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-bold text-sm text-[#0A0A0F]">
                        {evt.event_type === 'email' ? `Email: ${evt.subject}` : evt.title}
                      </div>
                      <div className="text-[10px] font-mono text-[#9A9AA3]">{timeAgo(evt.date)}</div>
                    </div>
                    
                    <div className="text-xs text-[#5C5C66]">
                      {evt.event_type === 'email' ? (
                        <div className="line-clamp-2 mt-1">{evt.body}</div>
                      ) : (
                        <div className="mt-1">{evt.detail}</div>
                      )}
                    </div>
                    
                    {/* Status badges for emails */}
                    {evt.event_type === 'email' && (
                      <div className="mt-3 flex gap-2 flex-wrap">
                        {evt.sent && <Badge className="text-[9px] bg-green-50 text-green-700 border-green-200">SENT</Badge>}
                        {evt.opened && <Badge className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">OPENED</Badge>}
                        {evt.clicked && <Badge className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200">CLICKED</Badge>}
                        {evt.replied && <Badge className="text-[9px] bg-orange-50 text-orange-700 border-orange-200">REPLIED</Badge>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: Notes & Tasks & Composer */}
        <div className="lg:col-span-4 space-y-5">
          {/* Notes */}
          <div className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
            <div className="p-4 border-b border-[#EAEAE3] bg-[#FAFAF7] flex items-center justify-between">
              <div className="font-display font-bold flex items-center gap-2"><FileText className="h-4 w-4 text-[#002FA7]" /> Notes</div>
            </div>
            <div className="p-4">
              <div className="flex gap-2 mb-4">
                <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Type a note..." className="bg-[#FAFAF7]" onKeyDown={e => e.key === 'Enter' && addNote()} />
                <Button onClick={addNote} size="icon" className="bg-[#0A0A0F] text-white shrink-0"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="space-y-3">
                {notes.map(note => (
                  <div key={note.id} className={`p-3 rounded-xl border ${note.pinned ? 'bg-amber-50/50 border-amber-200' : 'bg-white border-[#EAEAE3]'} relative group`}>
                    <p className="text-sm text-[#0A0A0F] whitespace-pre-wrap pr-6">{note.body}</p>
                    <div className="mt-2 text-[10px] font-mono text-[#9A9AA3]">{timeAgo(note.created_at)}</div>
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => togglePinNote(note.id, note.pinned)} className={`p-1 rounded ${note.pinned ? 'text-amber-500' : 'text-[#9A9AA3] hover:text-[#0A0A0F]'}`}>
                        <Pin className="h-3 w-3" />
                      </button>
                      <button onClick={() => deleteNote(note.id)} className="p-1 rounded text-red-400 hover:text-red-600">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tasks */}
          <div className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
            <div className="p-4 border-b border-[#EAEAE3] bg-[#FAFAF7] flex items-center justify-between">
              <div className="font-display font-bold flex items-center gap-2"><CheckSquare className="h-4 w-4 text-[#002FA7]" /> Tasks</div>
            </div>
            <div className="p-4">
              <div className="flex gap-2 mb-4">
                <Input value={newTask} onChange={e => setNewTask(e.target.value)} placeholder="Follow up next week..." className="bg-[#FAFAF7]" onKeyDown={e => e.key === 'Enter' && addTask()} />
                <Button onClick={addTask} size="icon" className="bg-[#0A0A0F] text-white shrink-0"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="space-y-2">
                {tasks.map(task => (
                  <div key={task.id} className={`flex items-start gap-3 p-3 rounded-xl border ${task.completed ? 'bg-[#FAFAF7] border-transparent opacity-60' : 'bg-white border-[#EAEAE3]'}`}>
                    <button onClick={() => toggleTask(task.id, task.completed)} className="mt-0.5 text-[#002FA7] shrink-0">
                      {task.completed ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                    </button>
                    <div className="min-w-0">
                      <div className={`text-sm ${task.completed ? 'line-through text-[#9A9AA3]' : 'text-[#0A0A0F]'}`}>{task.title}</div>
                      {task.due_date && <div className="text-[10px] font-mono text-red-500 mt-1 flex items-center gap-1"><Calendar className="h-3 w-3" /> {task.due_date}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Outreach Composer */}
          <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-mono">— Outreach composer</div>
                <div className="font-display text-xl font-bold tracking-tighter">Draft a cold email</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-[#F4F4EE] flex items-center justify-center text-[#002FA7]"><Sparkles className="h-5 w-5" /></div>
            </div>
            <div className="space-y-4 mb-4">
              <div>
                <Label className="label-mono text-xs">Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="mt-1.5 rounded-lg bg-[#FAFAF7]" data-testid="outreach-tone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["professional", "casual", "bold", "friendly"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-mono text-xs">Your offer / value prop</Label>
                <Input value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Pipeline platform that books 30 calls/mo" className="mt-1.5 rounded-lg bg-[#FAFAF7]" data-testid="outreach-offer" />
              </div>
            </div>
            <Button onClick={generate} disabled={generating} className="w-full rounded-lg bg-[#002FA7] hover:bg-[#1E4FD6] text-white" data-testid="generate-outreach-btn">
              {generating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Drafting...</>) : (<><Sparkles className="h-4 w-4 mr-2" /> Draft email</>)}
            </Button>
          </div>

          {/* Drafts & Emails List */}
          {emails.length > 0 && (
            <div className="space-y-4">
              {emails.map((e) => (
                <div key={e.id} className="bg-white border border-[#EAEAE3] rounded-2xl p-5 shadow-soft">
                  <div className="label-mono mb-2 flex items-center gap-2 flex-wrap">
                    {new Date(e.created_at).toLocaleDateString()}
                    {e.sent && <Badge className="text-[9px] bg-green-50 text-green-700">SENT</Badge>}
                    {e.opened && <Badge className="text-[9px] bg-purple-50 text-purple-700">OPENED</Badge>}
                    {e.send_error && !e.bounced && <Badge className="text-[9px] bg-red-50 text-red-700">FAILED</Badge>}
                  </div>
                  <div className="font-bold text-sm mb-3 truncate">{e.subject}</div>
                  
                  <div className="flex items-center gap-2">
                    {!e.sent && (
                      <Button onClick={() => sendEmail(e.id)} disabled={sendingId === e.id} size="sm" className="flex-1 rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white">
                        {sendingId === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-2" />} Send
                      </Button>
                    )}
                    <Button onClick={() => copy(`Subject: ${e.subject}\n\n${e.body}`)} variant="outline" size="sm" className="rounded-lg border-[#EAEAE3]">
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, link }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-3.5 w-3.5 text-[#9A9AA3] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="label-mono text-[10px]">{label}</div>
        {link ? (
          <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline break-all">{value}</a>
        ) : (
          <div className="break-all">{value}</div>
        )}
      </div>
    </div>
  );
}
